import {
  CREATE_CAMPAIGN_EDITOR_PATH,
  CREATE_CAMPAIGN_LIST_PATH,
  CREATE_CAMPAIGN_PENDING_GENERATION_KEY,
  CREATE_CAMPAIGN_PHASE,
  CREATE_CAMPAIGN_QUERY_PARAM,
  CREATE_CAMPAIGN_REQUIRED_MESSAGE_KEY,
  INITIAL_CREATIVES_QUERY_PARAM,
  RESUME_CAMPAIGN_ONBOARDING_QUERY_PARAM,
} from "./createAdsCampaignWizardConstants.js";

export const hasCampaignLocations = (locations) =>
  Boolean(locations?.locations?.length);

export const canSelectCreateCampaignPlatform = (formState) =>
  Boolean(formState?.language) && hasCampaignLocations(formState?.locations);

export const getSetupValidationMessage = (formState, t) => {
  if (canSelectCreateCampaignPlatform(formState)) {
    return null;
  }
  return t(CREATE_CAMPAIGN_REQUIRED_MESSAGE_KEY);
};

export const hasUnsavedCreateCampaignProgress = ({
  formState,
  phase,
  selectedPlatform,
} = {}) => {
  if (phase && phase !== CREATE_CAMPAIGN_PHASE.SETUP) {
    return true;
  }
  if (selectedPlatform) {
    return true;
  }
  if (String(formState?.context || "").trim()) {
    return true;
  }
  if (formState?.attachmentsFiles?.length > 0) {
    return true;
  }
  if (hasCampaignLocations(formState?.locations)) {
    return true;
  }
  return false;
};

export const buildCreateCampaignListUrl = ({ initialCreatives } = {}) => {
  const params = new URLSearchParams();
  params.set(CREATE_CAMPAIGN_QUERY_PARAM, "1");
  if (initialCreatives) {
    params.set(INITIAL_CREATIVES_QUERY_PARAM, initialCreatives);
  }
  return `${CREATE_CAMPAIGN_LIST_PATH}?${params.toString()}`;
};

export const buildCreateCampaignEditorUrl = ({ campaignID, platform } = {}) => {
  const params = new URLSearchParams();
  if (campaignID) {
    params.set("campaignID", campaignID);
  }
  if (platform) {
    params.set("platform", platform);
  }
  const query = params.toString();
  return query
    ? `${CREATE_CAMPAIGN_EDITOR_PATH}?${query}`
    : CREATE_CAMPAIGN_EDITOR_PATH;
};

export const consumeCreateCampaignSearchParams = (searchParams) => {
  const shouldOpen =
    searchParams?.get?.(CREATE_CAMPAIGN_QUERY_PARAM) === "1" ||
    Boolean(searchParams?.get?.(INITIAL_CREATIVES_QUERY_PARAM));
  const initialCreatives =
    searchParams?.get?.(INITIAL_CREATIVES_QUERY_PARAM) || null;

  return {
    shouldOpen,
    initialCreatives,
  };
};

export const stripCreateCampaignSearchParams = (searchParams) => {
  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete(CREATE_CAMPAIGN_QUERY_PARAM);
  nextParams.delete(INITIAL_CREATIVES_QUERY_PARAM);
  return nextParams;
};

export const buildPendingInitialGenerationState = ({
  campaignID,
  setupFields,
  platformData,
} = {}) => ({
  [CREATE_CAMPAIGN_PENDING_GENERATION_KEY]: {
    campaignID,
    setupFields,
    platformData,
  },
});

export const buildResumeCampaignOnboardingUrl = (alertID) => {
  const params = new URLSearchParams();
  params.set(RESUME_CAMPAIGN_ONBOARDING_QUERY_PARAM, alertID);
  return `${CREATE_CAMPAIGN_LIST_PATH}?${params.toString()}`;
};

export const stripResumeCampaignSearchParam = (searchParams) => {
  const nextParams = new URLSearchParams(searchParams);
  nextParams.delete(RESUME_CAMPAIGN_ONBOARDING_QUERY_PARAM);
  return nextParams;
};

const EMPTY_RESUME_LOCATIONS = { contexts: [], locations: [] };

export const getCampaignOnboardingResumeFromAlert = (alert) => {
  const platformData = alert?.platformGeneratingData || {};
  const currentCampaign = alert?.currentCampaign || {};
  const platform =
    platformData.platform || alert?.platform || currentCampaign?.platforms?.[0];

  if (!alert?.campaignID || !platform) {
    return null;
  }

  return {
    campaignID: alert.campaignID,
    platform,
    setupFields: {
      context: platformData.context || currentCampaign.context || "",
      language: platformData.language || currentCampaign.language || "",
      locations:
        platformData.locations ||
        currentCampaign.locations ||
        EMPTY_RESUME_LOCATIONS,
      attachmentsFiles:
        platformData.attachmentsFiles || currentCampaign.attachmentsFiles || [],
      fileNamesMapping:
        platformData.fileNamesMapping || currentCampaign.fileNamesMapping || {},
    },
    platformData: {
      ...platformData,
      platform,
    },
  };
};

export const consumePendingInitialGenerationState = (locationState) => {
  const pending = locationState?.[CREATE_CAMPAIGN_PENDING_GENERATION_KEY];
  if (!pending?.campaignID || !pending?.platformData) {
    return null;
  }
  return pending;
};
